import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { axiosPrivate } from 'app/utils';
import useEventTracker from 'app/hooks/useEventTracker';
import { WORKFLOW_DASHBOARD_EVENTS } from 'app/tracking';

import { Tabs } from 'app/shared-components/structure';
import { useHeaderActions, useSelectedPharmacy } from 'app/hooks';

const tabs = [
  {
    label: 'Overview',
    value: 'overview',
    path: '/dashboard/overview',
  },
  {
    label: 'Prescriptions',
    value: 'prescriptions',
    path: '/dashboard/prescriptions',
  },
  {
    label: 'Services',
    value: 'services',
    path: '/dashboard/services',
  },
  {
    label: 'Competitor Comparison (New)',
    value: 'competitor-comparison',
    path: '/dashboard/competitor-comparison',
  },
];

async function getOrganisationsForPharmacist(pharmacistId) {
  const response = await axiosPrivate.get(`/pharmacists/${pharmacistId}/organisations`);
  return response.data.data?.organisations?.sort((a, b) => a.name.localeCompare(b.name)) ?? [];
}

export default function DashboardLayout({ children }) {
  const trackEvent = useEventTracker();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedPharmacy = useSelectedPharmacy();
  const { user } = useSelector((state) => state.auth);

  // TODO: This is product of Friday eod release. This code was rushed and should be refactored.
  const selectedOrganisationId = searchParams.get('organisationId') || '';
  const { data: organisations } = useQuery({
    queryKey: ['pharmacists', user?._id, 'organisations'],
    queryFn: () => getOrganisationsForPharmacist(user?._id),
    enabled: Boolean(user?._id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    meta: { errorMessage: 'Failed to load organisations' },
  });

  const selectedOrganisation = useMemo(() => {
    return organisations?.find((organisation) => organisation._id === selectedOrganisationId);
  }, [organisations, selectedOrganisationId]);

  const headerActions = useMemo(() => {
    if (!selectedOrganisation?.name || !selectedOrganisation?._id) return null;

    return [
      {
        title: 'We’d love to hear your feedback - please click here',
        type: 'link',
        url: `https://healthera.typeform.com/to/E2Rqcbwk#org_name=${selectedOrganisation.name}&id=${selectedOrganisation._id}`,
      },
    ];
  }, [selectedOrganisation?.name, selectedOrganisation?._id]);

  useHeaderActions(headerActions);

  const selectedTab = useMemo(() => {
    const currentPath = location.pathname;
    return tabs.find((tab) => currentPath.endsWith(tab.path))?.value || 'overview';
  }, [location.pathname]);

  // Track tab_loaded once when the dashboard layout mounts based on current section
  useEffect(() => {
    const tabLoadedEventMap = {
      overview: WORKFLOW_DASHBOARD_EVENTS.OVERVIEW.TAB_LOADED,
      prescriptions: WORKFLOW_DASHBOARD_EVENTS.PRESCRIPTIONS.TAB_LOADED,
      services: WORKFLOW_DASHBOARD_EVENTS.SERVICES.TAB_LOADED,
      'competitor-comparison': WORKFLOW_DASHBOARD_EVENTS.COMPETITOR_COMPARISON.TAB_LOADED,
    };

    const tabLoadedEvent = tabLoadedEventMap[selectedTab];
    if (tabLoadedEvent) {
      trackEvent(tabLoadedEvent.action, tabLoadedEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - fires only once on mount

  useEffect(() => {
    const currentOrganisationId = searchParams.get('organisationId');
    const pharmacyOrganisationId = selectedPharmacy?.chain_id;

    if (!currentOrganisationId && pharmacyOrganisationId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('organisationId', pharmacyOrganisationId);
      navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });

      // Track organization selected (auto-selected on mount)
      trackEvent(
        WORKFLOW_DASHBOARD_EVENTS.ORG_SELECTED.action,
        WORKFLOW_DASHBOARD_EVENTS.ORG_SELECTED
      );
    }
  }, [selectedPharmacy?.chain_id, searchParams, location.pathname, navigate, trackEvent]);

  function handleTabChange(event, newValue) {
    const newPath = tabs.find((tab) => tab.value === newValue)?.path;

    if (!newPath) return;

    // Track section clicked using lookup map
    const sectionEventMap = {
      overview: WORKFLOW_DASHBOARD_EVENTS.OVERVIEW.TAB_CLICKED,
      prescriptions: WORKFLOW_DASHBOARD_EVENTS.PRESCRIPTIONS.TAB_CLICKED,
      services: WORKFLOW_DASHBOARD_EVENTS.SERVICES.TAB_CLICKED,
    };

    const sectionEvent = sectionEventMap[newValue];
    if (sectionEvent) {
      trackEvent(sectionEvent.action, sectionEvent);
    }

    const currentParams = new URLSearchParams(searchParams);
    const organisationId = currentParams.get('organisationId') || selectedPharmacy?.chain_id;

    const newUrl = organisationId ? `${newPath}?organisationId=${organisationId}` : newPath;
    navigate(newUrl, { replace: true });
  }

  return (
    <Box className="grow flex flex-col px-20 py-16 gap-16 bg-white overflow-hidden">
      <Box className="flex items-center">
        <Tabs tabs={tabs} selectedTab={selectedTab} handleChange={handleTabChange} />
      </Box>
      {children}
    </Box>
  );
}
